// src/components/chat/ChatBell.jsx - ПОЛНОСТЬЮ ИСПРАВЛЕННАЯ ВЕРСИЯ
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { 
  Badge, 
  Dropdown, 
  ListGroup, 
  Button, 
  Spinner
} from 'react-bootstrap';
import { useChat } from '../../contexts/ChatContext';
import { useNavigate } from 'react-router-dom';

const ChatBell = ({ isMobile = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  
  const { 
    rooms, 
    unreadCounts, 
    totalUnreadCount,
    loadingRooms,
    selectRoom
  } = useChat();

  // Логируем для отладки
  useEffect(() => {
    console.log('🔔 ChatBell - rooms with unread:', rooms.filter(r => unreadCounts[r.id] > 0).map(r => ({ id: r.id, name: getRoomName(r), unread: unreadCounts[r.id] })));
    console.log('🔔 ChatBell - totalUnreadCount:', totalUnreadCount);
  }, [rooms, unreadCounts, totalUnreadCount]);

  // Закрытие dropdown при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBellClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleChatClick = (room) => {
    setShowDropdown(false);
    selectRoom(room);
    navigate('/_admin/chat');
  };

  const handleViewAll = () => {
    setShowDropdown(false);
    navigate('/_admin/chat');
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'только что';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    if (diffDays < 7) return `${diffDays} дн назад`;
    
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short'
    });
  };

  const getRoomName = (room) => {
    if (room.type === 'group') return room.name || 'Группа';
    if (room.type === 'task') return `Задача #${room.task_id}`;
    
    // Для личных диалогов пытаемся получить имя собеседника
    if (room.participants && room.participants.length > 0) {
      // Находим не текущего пользователя (но у нас нет current user в этом компоненте)
      // Поэтому используем last_message?.user_name или other_user_name если есть
      if (room.other_user_name) return room.other_user_name;
    }
    
    return 'Диалог';
  };

  const getRoomIcon = (room) => {
    if (room.type === 'group') return 'bi-people';
    if (room.type === 'task') return 'bi-list-task';
    return 'bi-person';
  };

  const getLastMessagePreview = (room) => {
    if (!room.last_message) return 'Нет сообщений';
    const content = room.last_message.content || '';
    return content.length > 40 ? content.substring(0, 40) + '...' : content;
  };

  // Получаем комнаты с непрочитанными сообщениями (максимум 5)
  const roomsWithUnread = useMemo(() => {
    // Сначала находим все комнаты с непрочитанными
    const roomsWithUnreadList = rooms
      .filter(room => unreadCounts[room.id] > 0)
      .map(room => ({
        ...room,
        unreadCount: unreadCounts[room.id]
      }));
    
    console.log('🔍 Rooms with unread:', roomsWithUnreadList);
    
    // Сортируем по дате последнего сообщения (новые сверху)
    return roomsWithUnreadList
      .sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;
      })
      .slice(0, 5);
  }, [rooms, unreadCounts]);

  // Если нет комнат с непрочитанными, но есть общий счетчик, это ошибка
  useEffect(() => {
    if (totalUnreadCount > 0 && roomsWithUnread.length === 0) {
      console.warn('⚠️ totalUnreadCount > 0 but no rooms with unread messages found', {
        totalUnreadCount,
        unreadCounts,
        rooms: rooms.map(r => ({ id: r.id, name: r.name }))
      });
    }
  }, [totalUnreadCount, roomsWithUnread.length, unreadCounts, rooms]);

  return (
    <div className="position-relative chat-bell" ref={dropdownRef}>
      {/* Кнопка колокольчика чата */}
      <button
        type="button"
        className="notification-bell-btn position-relative d-flex align-items-center justify-content-center"
        onClick={handleBellClick}
        aria-label="Чаты"
        aria-expanded={showDropdown}
      >
        <i className={`bi ${totalUnreadCount > 0 ? 'bi-chat-fill text-primary' : 'bi-chat'} notification-bell-icon`}></i>
        
        {/* Бейдж с количеством непрочитанных */}
        {totalUnreadCount > 0 && (
          <Badge 
            bg="danger" 
            className="position-absolute notification-badge"
          >
            {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
          </Badge>
        )}
      </button>

      {/* Dropdown с чатами */}
      <Dropdown show={showDropdown} className="position-absolute" style={{ 
        top: '100%', 
        [isMobile ? 'right' : 'right']: '0', 
        zIndex: 1060 
      }}>
        <Dropdown.Menu 
          show 
          className="shadow border-0 mt-2"
          style={{ 
            width: isMobile ? '95vw' : '380px',
            maxWidth: isMobile ? '400px' : '95vw',
            maxHeight: isMobile ? '70vh' : '500px',
            overflow: 'hidden',
            [isMobile ? 'right' : 'right']: isMobile ? '10px' : '0',
            left: isMobile ? 'auto' : 'auto'
          }}
        >
          {/* Заголовок */}
          <div className="d-flex justify-content-between align-items-center p-4 border-bottom bg-light">
            <div>
              <h6 className="mb-0 fw-bold">Сообщения</h6>
              <small className="text-muted">
                {totalUnreadCount > 0 
                  ? `${totalUnreadCount} непрочитанных` 
                  : 'Новых сообщений нет'
                }
              </small>
            </div>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={handleViewAll}
              title="Все чаты"
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: '32px', height: '32px' }}
            >
              <i className="bi bi-list"></i>
            </Button>
          </div>

          {/* Список чатов */}
          <div style={{ maxHeight: isMobile ? '60vh' : '400px', overflowY: 'auto' }}>
            {loadingRooms ? (
              <div className="text-center p-5">
                <Spinner animation="border" variant="primary" />
                <div className="mt-3 text-muted">Загрузка чатов...</div>
              </div>
            ) : roomsWithUnread.length === 0 ? (
              <div className="text-center p-5 text-muted">
                <i className="bi bi-chat display-4 mb-3 opacity-50"></i>
                <div className="fw-medium">Нет новых сообщений</div>
                <small>Здесь появятся чаты с непрочитанными сообщениями</small>
              </div>
            ) : (
              <ListGroup variant="flush">
                {roomsWithUnread.map(room => (
                  <ListGroup.Item
                    key={room.id}
                    className="border-0 p-0 chat-item"
                    onClick={() => handleChatClick(room)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="p-3 position-relative">
                      <div className="d-flex gap-3">
                        <div className="flex-shrink-0 position-relative">
                          <div 
                            className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center"
                            style={{ width: '48px', height: '48px' }}
                          >
                            <i className={`bi ${getRoomIcon(room)} fs-4 text-primary`}></i>
                          </div>
                          {room.is_locked && (
                            <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle">
                              <span className="visually-hidden">Locked</span>
                            </span>
                          )}
                          {/* Индикатор непрочитанных на иконке */}
                          {unreadCounts[room.id] > 0 && (
                            <span className="position-absolute top-0 start-0 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem' }}>
                              {unreadCounts[room.id]}
                            </span>
                          )}
                        </div>
                        <div className="flex-grow-1 min-w-0">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <h6 className="mb-0 fw-semibold text-truncate" style={{ maxWidth: '180px' }}>
                              {getRoomName(room)}
                            </h6>
                            <small className="text-muted flex-shrink-0 ms-2">
                              {formatTime(room.updated_at || room.created_at)}
                            </small>
                          </div>
                          <p className="text-muted small text-truncate mb-1">
                            {getLastMessagePreview(room)}
                          </p>
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              {room.type === 'group' ? `${room.participants?.length || 0} участников` : ''}
                            </small>
                            {unreadCounts[room.id] > 1 && (
                              <Badge bg="danger" pill>
                                +{unreadCounts[room.id] - 1}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>

          {/* Футер */}
          <div className="p-3 border-top bg-light">
            <Button 
              variant="outline-primary" 
              size="sm" 
              className="w-100 d-flex align-items-center justify-content-center gap-2"
              onClick={handleViewAll}
            >
              <span>Все чаты</span>
              <i className="bi bi-arrow-right"></i>
            </Button>
          </div>
        </Dropdown.Menu>
      </Dropdown>

      <style>{`
        .chat-item:hover {
          background-color: rgba(141, 192, 251, 0.1);
        }
        .chat-item {
          transition: all 0.2s ease;
        }
      `}</style>
    </div>
  );
};

export default ChatBell;